/**
 * Nami observations pool — the closed source/epistemic unions (NAMI_KEEPER PR1
 * design doc §3; supabase/migrations/20260910120000_nami_observations.sql).
 *
 * `source` drives the authority ladder that resolves conflicting facts: WHO saw
 * the observation. Before this file existed the union was FICTION — every live
 * depositor hardcoded 'task_firsthand', which is not a member, so every
 * conflict resolved on nothing, for months, unnoticed. These two unions are a
 * closed, 5- and 9-member set respectively, checked at the DB layer via CHECK
 * constraints in the migration above. Keep this file member-for-member in
 * sync with that migration — drift here means an insert that passes the
 * TypeScript type but fails the CHECK constraint at runtime.
 */

/** WHO saw the observation. Closed, 5-member union — mirrors the `source` CHECK. */
export const NAMI_SOURCES = [
  'user_direct',
  'upload',
  'screen',
  'tool_observed',
  'agent_inferred',
] as const;

export type NamiSource = (typeof NAMI_SOURCES)[number];

/** WHAT KIND of claim. Closed, 9-member union — mirrors the `epistemic` CHECK. */
export const NAMI_EPISTEMICS = [
  'identity',
  'relationship',
  'commitment',
  'preference',
  'project_state',
  'correction',
  'retraction',
  'procedure',
  'other',
] as const;

export type NamiEpistemic = (typeof NAMI_EPISTEMICS)[number];

/**
 * WHO WROTE THE ROW — the SERVER-SET authorship stamp. Closed, 2-member union,
 * mirroring `nami_observations.authored_by` (nullable text, no CHECK — see
 * supabase/migrations/20261101090000_nami_observations_authored_by.sql).
 *
 * This is NOT a second `source`. `source` answers "who saw it" AS THE DEPOSITOR
 * CLAIMS; on the sandbox rail the depositor is a model, and the token is whatever
 * that model wrote. `authored_by` answers "which door did this row come through",
 * which only the server can know and the sandbox cannot state:
 *
 *   'user'  — a HUMAN acted at a server route: a memory edit, a digest verdict, a
 *             proposal accepted or refined. The words or the choice are the person's.
 *   'agent' — the sandbox wire (`remember`, the end-of-task memory sweep) or any
 *             server-side inference lane. The model chose the words AND the tags.
 *
 * NULL is legitimate and means UNKNOWN: rows written before this column existed, and
 * the one-off backfill script that inserts straight into the table. Unknown is never
 * treated as the user's — see `isUserAuthored`.
 */
export const NAMI_AUTHORS = ['user', 'agent'] as const;

export type NamiAuthor = (typeof NAMI_AUTHORS)[number];

export function isNamiSource(x: unknown): x is NamiSource {
  return typeof x === 'string' && (NAMI_SOURCES as readonly string[]).includes(x);
}

export function isNamiAuthor(x: unknown): x is NamiAuthor {
  return typeof x === 'string' && (NAMI_AUTHORS as readonly string[]).includes(x);
}

export function isNamiEpistemic(x: unknown): x is NamiEpistemic {
  return typeof x === 'string' && (NAMI_EPISTEMICS as readonly string[]).includes(x);
}

/**
 * WHOSE ACTION DOES THE CLAIM REST ON — the one rule that binds the two unions.
 *
 * A `procedure` is not a fact about the user, it is a STANDING INSTRUCTION about how
 * they want recurring work carried out, and the procedures file is loaded before every
 * task. A claim with that reach must be grounded in something the USER said or chose.
 * `agent_inferred` is the union's own word for "the agent worked it out itself rather
 * than being told" (see the `remember` tool's `source` copy), so an agent_inferred
 * procedure is the agent's own habit wearing the user's voice.
 *
 * THE INCIDENT (2026-08-22) this exists to prevent: a drill ran the same "summarize
 * this and email a two-line summary" task three times. The end-of-task sweep watched
 * the AGENT's own repeated action and deposited it as a procedure, it folded into the
 * user's "How I work for you" file, and eight hours later the agent emailed the founder
 * on a task that had asked only for an in-app summary (cited row by row in
 * supabase/migrations/20261101090000_nami_observations_authored_by.sql; the original
 * wording here, "said not to touch the mailbox at all", was slightly stronger than the
 * instruction actually was). He had never
 * expressed the preference; the agent had merely done the thing, repeatedly, because it
 * was told to each time. Repetition of the agent's own behaviour is not evidence about
 * the person, and no repetition threshold fixes that: three times zero is still zero.
 *
 * DEMOTION, NOT A DROP. The sentence still deposits, as 'other' — the pool is
 * append-only truth and the observation is a real thing the agent believed. It simply
 * cannot enter the `{ only: 'procedure' }` slice the procedures fold sweeps, so it never
 * becomes a rule. This also mirrors the historic behaviour: until 2026-08-21 the sweep
 * could not mint `procedure` at all and every procedural fact landed as 'other'.
 *
 * `source` is NEVER rewritten. Relabeling provenance so a claim fits is the disease the
 * closed union exists to cure; only the CLAIM is lowered, and only ever downward.
 *
 * Pure and idempotent, so it may be applied at more than one seam (the relay wire
 * boundary, where the counter and task context live, and `depositToPool`, the funnel
 * every producer goes through) without the two being a mirrored constant that can drift.
 */
export function groundEpistemic(source: NamiSource, epistemic: NamiEpistemic): NamiEpistemic {
  if (epistemic === 'procedure' && source === 'agent_inferred') return 'other';
  return epistemic;
}

/**
 * WHOSE HAND WROTE THIS ROW — the predicate `groundEpistemic` could not express.
 *
 * WHY A SECOND RULE EXISTS. `groundEpistemic` (above) is sound and it is not bypassed;
 * its predicate is simply `source === 'agent_inferred'`, and on the only rail that mints
 * procedures the `source` token is chosen BY THE SAME MODEL whose behaviour is the thing
 * being laundered. It therefore catches exactly the honest case — the model admitting "I
 * worked this out myself" — and nothing else. On 2026-08-23, AFTER it shipped, the founder's
 * standing-rules file refilled twice (v6 570 chars 12:56Z, v7 652 chars 16:42Z) from three
 * `procedure` observations the sweep had stamped `source: 'user_direct'`. Same laundering,
 * one relabel away, guard untouched and unfired. A self-reported provenance field cannot
 * gate a claim about the reporter.
 *
 * SO THE PREDICATE MOVES OFF WHAT WAS CLAIMED AND ONTO WHAT WAS DONE. `authored_by` is
 * stamped by the server from the code path that wrote the row, is never read off the wire,
 * and no prompt or model output can reach it.
 *
 * UNKNOWN IS NOT THE USER. `null` (a legacy row, a direct-insert script) reads FALSE. That
 * is the fail-closed direction: the cost of a wrong `false` is one standing rule the user
 * has to state again; the cost of a wrong `true` is the assistant acting on a habit nobody
 * asked for, which is the incident this whole rail exists to prevent.
 *
 * Pure and total, so the fold can re-decide it on EVERY run from the row's persisted
 * stamp. That matters: a deposit-time demotion only ever stops NEW poison, which is why the
 * file refilled from rows already in the pool. This is the level-triggered half.
 */
export function isUserAuthored(authoredBy: NamiAuthor | null | undefined): boolean {
  return authoredBy === 'user';
}

/** A nami_observations row — one first-hand observation in the durable pool. */
export interface Observation {
  /**
   * 'fh-<uuid>', minted by the depositor (not DB-generated).
   * 'dv-<uuid>' = a digest-verdict deposit, minted in nami-digest.service.
   * A prefix is conventional, not guaranteed — the calendar series deposit mints a bare
   * deterministic uuidv5. Never parse this id; read the row's fields.
   */
  id: string;
  user_id: string;
  /** One plain sentence — the observation itself. */
  text: string;
  source: NamiSource;
  epistemic: NamiEpistemic;
  /**
   * WHICH DOOR the row came through, stamped by the server, never by the wire.
   * NULL = written before the column existed (or by the direct-insert backfill
   * script) — unknown authorship, which `isUserAuthored` reads as NOT the user's.
   */
  authored_by: NamiAuthor | null;
  /** The ORIGINAL observation time, never the ingest time. */
  observed_at: string;
  /** The task the observation was made during, if any. */
  task_id: string | null;
  /** Non-null once folded into a projection (user.md etc.); null = unfolded, served by recall. */
  folded_at: string | null;
  /** Ingest time (DB-assigned), distinct from observed_at. */
  created_at: string;
}
